// Login.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { APP_CONFIG } from "../app-config";
import AppIcon from "../assets/image/icon.png";
import { FaMapMarkerAlt, FaSpinner, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import { MdLocationOn, MdGpsFixed } from 'react-icons/md';
import { HiLocationMarker } from 'react-icons/hi';

const COLORS = APP_CONFIG.colors;
const URLS = APP_CONFIG.urls;

function Login() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [loading, setIsLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const inputRefs = useRef([]);

  // Location state
  const [location, setLocation] = useState({
    address: { city: '', state: '', country: '' },
    latitude: null,
    longitude: null,
    altitude: null,
    isLoading: true,
    error: null
  });

  const maskedMobile = mobile
    ? mobile.slice(0, 2) + "****" + mobile.slice(6)
    : "XXXXXXXXXX";

  // Get location on mount
  useEffect(() => {
    getLocation();
  }, []);

  const getLocation = () => {
    setLocation(prev => ({ ...prev, isLoading: true }));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, altitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            setLocation({
              address: {
                city: data.address?.city || data.address?.town || data.address?.village || '',
                state: data.address?.state || '',
                country: data.address?.country || ''
              },
              latitude,
              longitude,
              altitude: altitude || null,
              isLoading: false,
              error: null
            });
          } catch (error) {
            setLocation({
              address: { city: '', state: '', country: '' },
              latitude,
              longitude,
              altitude: altitude || null,
              isLoading: false,
              error: 'Failed to get address details'
            });
          }
        },
        (error) => {
          setLocation({
            address: { city: '', state: '', country: '' },
            latitude: null,
            longitude: null,
            altitude: null,
            isLoading: false,
            error: error.message
          });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      setLocation({
        address: { city: '', state: '', country: '' },
        latitude: null,
        longitude: null,
        altitude: null,
        isLoading: false,
        error: 'Geolocation is not supported'
      });
    }
  };

  // OTP Timer
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Auto-focus OTP input
  useEffect(() => {
    if (showOtp && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [showOtp]);

  // Auto-verify OTP
  useEffect(() => {
    const code = otp.join("");
    if (code.length === 6 && !verifying) {
      const timeout = setTimeout(() => {
        handleVerify();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [otp, verifying]);

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(val);
    if (error) setError("");
    // Reset OTP state if user changes number
    if (showOtp) {
      setShowOtp(false);
      setOtp(["", "", "", "", "", ""]);
      setOtpToken("");
      setTimer(30);
      setCanResend(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiCall(
        "login",
        {
          number: mobile,
          address: location.address.city || null,
          longitude: location.longitude || null,
          latitude: location.latitude || null,
          altitude: location.altitude || null
        },
        false
      );

      if (result.status === true) {
        setOtpToken(result.data.otp_token);
        setShowOtp(true);
        setTimer(30);
        setCanResend(false);
        toast.success("OTP sent successfully!");
      } else if (result.response_code === 3) {
        navigate(`/register`, { state: { mobile: mobile } });
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = useCallback(async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    setVerifying(true);

    try {
      const result = await apiCall(
        "verify-otp",
        {
          number: mobile,
          otp: code,
          otp_token: otpToken,
          address: location.address.city || null,
          longitude: location.longitude || null,
          latitude: location.latitude || null,
          altitude: location.altitude || null,
          prevPage: 'login'
        },
        false
      );

      if (result.status === true) {
        toast.success("OTP verified successfully!");
        localStorage.setItem("credentials", JSON.stringify(result.data));
        navigate("/home", { replace: true });
      } else {
        toast.error(result.message || "Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [otp, mobile, otpToken, location, navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (verifying) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    if (verifying) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend || verifying || resending) return;

    setResending(true);

    try {
      const result = await apiCall(
        "login",
        {
          number: mobile,
          address: location.address.city || null,
          longitude: location.longitude || null,
          latitude: location.latitude || null,
          altitude: location.altitude || null
        },
        false
      );

      if (result.status === true) {
        setOtpToken(result.data.otp_token);
        toast.success("OTP resent successfully!");
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        toast.error(result.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleChangeNumber = () => {
    setShowOtp(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpToken("");
    setTimer(30);
    setCanResend(false);
    setError("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-6 md:px-8"
      style={{
        background: COLORS.bgColor,
      }}
    >
      <div className="w-full max-w-[480px] mx-auto">
        {/* Login Card */}
        <div
          className="flex flex-col items-center w-full"
          style={{
            padding: "32px 20px",
            borderRadius: COLORS.borderRadius,
            background: COLORS.bgColor,
            boxShadow: "0 20px 60px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)",
            border: `1px solid ${COLORS.borderColor}`,
          }}
        >
          {/* Logo */}
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mb-3 sm:mb-4"
            style={{
              background: `linear-gradient(135deg, ${COLORS.primaryLight}, ${COLORS.bgColor})`,
              border: `1px solid ${COLORS.primaryColor}30`,
            }}
          >
            <img
              src={AppIcon}
              alt="App Icon"
              className="rounded-xl w-14 h-14 sm:w-16 sm:h-16 object-contain"
            />
          </div>

          <h1
            className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center"
            style={{
              color: COLORS.textPrimary,
            }}
          >
            {showOtp ? "Verify OTP" : "Welcome Back"}
          </h1>

          <p
            className="text-xs sm:text-sm mb-6 sm:mb-8 text-center"
            style={{
              color: COLORS.textSecondary,
            }}
          >
            {showOtp
              ? `OTP sent to +91 ${maskedMobile}`
              : "Enter your mobile number to continue"
            }
          </p>

          {!showOtp ? (
            // Login Form
            <form onSubmit={handleSubmit} className="w-full">
              <div
                className="flex items-center overflow-hidden transition-colors duration-200"
                style={{
                  border: `1px solid ${error ? COLORS.errorColor : COLORS.borderColor}`,
                  borderRadius: COLORS.borderRadius,
                  background: COLORS.bgColor,
                }}
              >
                <div
                  className="px-3 sm:px-4 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold whitespace-nowrap"
                  style={{
                    background: COLORS.surfaceColor,
                    color: COLORS.textPrimary,
                    borderRight: `1px solid ${COLORS.borderColor}`,
                  }}
                >
                  +91
                </div>
                <input
                  type="tel"
                  maxLength={10}
                  pattern="[0-9]*"
                  placeholder="10-digit mobile number"
                  value={mobile}
                  onChange={handleChange}
                  disabled={loading}
                  className="flex-1 px-3 sm:px-4 py-3 sm:py-3.5 border-none text-sm outline-none bg-transparent disabled:opacity-60 min-w-0"
                  style={{
                    color: COLORS.textPrimary,
                    fontSize: '16px', // Prevents zoom on mobile
                  }}
                />
              </div>
              {error && (
                <div
                  className="text-xs mt-1.5"
                  style={{
                    color: COLORS.errorColor,
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 sm:h-12 border-none rounded-lg text-sm sm:text-[15px] font-semibold mt-6 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.98]"
                style={{
                  background: loading ? COLORS.primaryDark : COLORS.primaryColor,
                  color: COLORS.textOnPrimary,
                }}
                onMouseOver={(e) => {
                  if (!loading) e.currentTarget.style.background = COLORS.primaryDark;
                }}
                onMouseOut={(e) => {
                  if (!loading) e.currentTarget.style.background = COLORS.primaryColor;
                }}
                onTouchStart={(e) => {
                  if (!loading) e.currentTarget.style.transform = "scale(0.97)";
                }}
                onTouchEnd={(e) => {
                  if (!loading) e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full animate-spin"
                      style={{
                        border: `2px solid ${COLORS.textOnPrimary}`,
                        borderTop: `2px solid transparent`,
                      }}
                    />
                    Sending...
                  </div>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          ) : (
            // OTP Verification
            <div className="w-full">
              <div
                className="rounded-2xl p-4 sm:p-6 mb-4 flex flex-col items-center"
                style={{
                  background: COLORS.surfaceColor,
                  border: `1px solid ${COLORS.borderColor}`,
                }}
              >
                <div
                  className="flex gap-1.5 sm:gap-2 mb-4 justify-center w-full"
                  onPaste={handleOtpPaste}
                >
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="tel"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      disabled={verifying}
                      className="w-9 h-11 sm:w-[42px] sm:h-12 text-center text-lg sm:text-xl font-semibold rounded-xl outline-none transition-all duration-200 disabled:opacity-60"
                      style={{
                        border: `1.5px solid ${digit ? COLORS.primaryColor : COLORS.borderColor}`,
                        color: COLORS.textPrimary,
                        background: COLORS.bgColor,
                        fontSize: '18px',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = COLORS.primaryColor;
                        e.target.style.boxShadow = `0 0 0 3px ${COLORS.primaryLight}40`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = digit ? COLORS.primaryColor : COLORS.borderColor;
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  ))}
                </div>

                <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 text-xs sm:text-[13px]">
                  <span style={{ color: COLORS.textSecondary }}>
                    {canResend ? (
                      <span
                        onClick={handleResend}
                        className={`font-semibold ${(verifying || resending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{ color: COLORS.primaryColor }}
                      >
                        {resending ? "Sending..." : "Resend OTP"}
                      </span>
                    ) : (
                      <span>
                        Resend in <span style={{ color: COLORS.primaryColor, fontWeight: 600 }}>{timer}s</span>
                      </span>
                    )}
                  </span>
                  <span
                    onClick={handleChangeNumber}
                    className="font-semibold cursor-pointer hover:opacity-80 transition-opacity text-xs sm:text-[13px]"
                    style={{ color: COLORS.primaryColor }}
                  >
                    Change Number
                  </span>
                </div>
              </div>

              <button
                onClick={handleVerify}
                disabled={verifying || otp.join("").length !== 6}
                className="w-full h-11 sm:h-12 border-none rounded-lg text-sm sm:text-[15px] font-semibold transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.98]"
                style={{
                  background: verifying ? COLORS.primaryDark : COLORS.primaryColor,
                  color: COLORS.textOnPrimary,
                }}
                onMouseOver={(e) => {
                  if (!verifying) e.currentTarget.style.background = COLORS.primaryDark;
                }}
                onMouseOut={(e) => {
                  if (!verifying) e.currentTarget.style.background = COLORS.primaryColor;
                }}
              >
                {verifying ? (
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full animate-spin"
                      style={{
                        border: `2px solid ${COLORS.textOnPrimary}`,
                        borderTop: `2px solid transparent`,
                      }}
                    />
                    Verifying...
                  </div>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          )}

          {/* Footer section */}
          <div
            className="w-full pt-6 flex flex-col items-center border-t"
            style={{
              borderColor: COLORS.borderColor,
              marginTop: "24px",
            }}
          >
            <div
              className="text-xs sm:text-sm text-center mb-2 sm:mb-3"
              style={{
                color: COLORS.textSecondary,
              }}
            >
              {!showOtp ? "New here? " : "Don't have an account? "}
              <span
                onClick={() => !loading && !verifying && navigate("/register")}
                className="font-semibold"
                style={{
                  color: COLORS.primaryColor,
                  cursor: !loading && !verifying ? "pointer" : "not-allowed",
                  opacity: loading || verifying ? 0.5 : 1,
                }}
              >
                Register
              </span>
            </div>

            <div
              className="text-[10px] sm:text-xs text-center leading-relaxed px-2"
              style={{
                color: COLORS.textSecondary,
              }}
            >
              By continuing you agree to our{" "}
              <span
                onClick={() => window.open(URLS.terms)}
                className="inline-block"
                style={{
                  color: COLORS.primaryColor,
                  cursor: !loading && !verifying ? "pointer" : "not-allowed",
                  opacity: loading || verifying ? 0.5 : 1,
                }}
              >
                Terms
              </span>{" "}
              &{" "}
              <span
                onClick={() => window.open(URLS.privacy)}
                className="inline-block"
                style={{
                  color: COLORS.primaryColor,
                  cursor: !loading && !verifying ? "pointer" : "not-allowed",
                  opacity: loading || verifying ? 0.5 : 1,
                }}
              >
                Privacy
              </span>
            </div>
          </div>
        </div>

        {/* Location Status - Directly below the login card with same width */}
        <div
          className="flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 w-full mt-3"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primaryColor} 0%, ${COLORS.primaryColor}dd 100%)`,
            border: `1px solid ${COLORS.borderColor}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Left Section - Label with Icon */}
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-white/20 rounded-full">
              <MdLocationOn className="text-white text-sm" />
            </div>
            <span className="text-white text-xs font-medium tracking-wide">
              Location
            </span>
          </div>

          {/* Right Section - Dynamic Content */}
          <div className="flex items-center gap-2">
            {location.address.city ? (
              <>
                <div className="text-right">
                  <div className="text-white text-xs font-medium leading-tight">
                    {location.address.city}
                  </div>
                  <div className="text-white/80 text-[10px] leading-tight">
                    {location.address.state}
                  </div>
                </div>
              </>
            ) : location.isLoading ? (
              <>
                <span className="text-white/90 text-xs font-medium">Fetching...</span>
                <FaSpinner className="text-white text-sm animate-spin" />
              </>
            ) : (
              <>
                <span className="text-white/80 text-xs">Unavailable</span>
                <FaExclamationCircle className="text-yellow-300 text-sm" />
              </>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  );
}

export default Login;